/**
 * Admin Homepage Health Dashboard
 * Shows real-time status of all homepage blocks
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  RefreshCw, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  ExternalLink,
  Settings,
  LayoutDashboard,
  Clock,
  Database,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHomepageHealth = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchHealthData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/homepage-health`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch health data: ${response.status}`);
      }

      const data = await response.json();
      setHealthData(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthData();
  }, [fetchHealthData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'empty':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2.5 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'ok':
        return <span className={`${baseClasses} bg-green-500/20 text-green-400`}>OK</span>;
      case 'empty':
        return <span className={`${baseClasses} bg-yellow-500/20 text-yellow-400`}>Empty</span>;
      case 'error':
        return <span className={`${baseClasses} bg-red-500/20 text-red-400`}>Error</span>;
      default:
        return <span className={`${baseClasses} bg-gray-500/20 text-gray-400`}>Unknown</span>;
    }
  };

  const getOverallStatusBadge = (status) => {
    const baseClasses = "px-3 py-1.5 rounded-lg text-sm font-semibold";
    switch (status) {
      case 'healthy':
        return <span className={`${baseClasses} bg-green-500/20 text-green-400 border border-green-500/30`}>✓ Healthy</span>;
      case 'warning':
        return <span className={`${baseClasses} bg-yellow-500/20 text-yellow-400 border border-yellow-500/30`}>⚠ Warning</span>;
      case 'degraded':
        return <span className={`${baseClasses} bg-red-500/20 text-red-400 border border-red-500/30`}>✗ Degraded</span>;
      case 'no_layout':
        return <span className={`${baseClasses} bg-gray-500/20 text-gray-400 border border-gray-500/30`}>No Layout</span>;
      default:
        return <span className={`${baseClasses} bg-gray-500/20 text-gray-400`}>Unknown</span>;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-6" data-testid="admin-homepage-health">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
            <span className="ml-3 text-lg">Loading health data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-6" data-testid="admin-homepage-health">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-red-400 mb-2">Health Check Failed</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={() => fetchHealthData()} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6" data-testid="admin-homepage-health">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin" 
              className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <LayoutDashboard className="w-7 h-7 text-orange-500" />
                Homepage Health
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Real-time status of all homepage blocks
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {getOverallStatusBadge(healthData?.status)}
            <Button 
              onClick={() => fetchHealthData(true)} 
              disabled={refreshing}
              variant="outline"
              className="border-gray-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {healthData?.summary && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-white">{healthData.summary.total}</div>
              <div className="text-sm text-gray-400">Total Blocks</div>
            </div>
            <div className="bg-gray-900/50 border border-green-500/20 rounded-xl p-4">
              <div className="text-3xl font-bold text-green-400">{healthData.summary.ok}</div>
              <div className="text-sm text-gray-400">OK</div>
            </div>
            <div className="bg-gray-900/50 border border-yellow-500/20 rounded-xl p-4">
              <div className="text-3xl font-bold text-yellow-400">{healthData.summary.empty}</div>
              <div className="text-sm text-gray-400">Empty</div>
            </div>
            <div className="bg-gray-900/50 border border-red-500/20 rounded-xl p-4">
              <div className="text-3xl font-bold text-red-400">{healthData.summary.error}</div>
              <div className="text-sm text-gray-400">Errors</div>
            </div>
          </div>
        )}

        {/* Last Checked Info */}
        <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Last checked: {lastRefresh ? formatTime(lastRefresh.toISOString()) : 'N/A'}
          </div>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Layout version: {healthData?.layout_version || 'N/A'}
          </div>
        </div>

        {/* Blocks Table */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="text-left py-4 px-5 text-sm font-semibold text-gray-400">Block</th>
                <th className="text-center py-4 px-5 text-sm font-semibold text-gray-400">Status</th>
                <th className="text-center py-4 px-5 text-sm font-semibold text-gray-400">Items</th>
                <th className="text-left py-4 px-5 text-sm font-semibold text-gray-400">Source</th>
                <th className="text-center py-4 px-5 text-sm font-semibold text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {healthData?.blocks?.map((block, index) => (
                <tr 
                  key={block.block_type} 
                  className={`border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors ${
                    index % 2 === 0 ? 'bg-gray-900/20' : ''
                  }`}
                  data-testid={`health-row-${block.block_type}`}
                >
                  {/* Block Name */}
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(block.status)}
                      <div>
                        <div className="font-medium text-white">{block.label}</div>
                        <div className="text-xs text-gray-500">{block.block_type}</div>
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="py-4 px-5 text-center">
                    {getStatusBadge(block.status)}
                  </td>

                  {/* Item Count */}
                  <td className="py-4 px-5 text-center">
                    <div className="font-mono text-lg font-semibold text-white">
                      {block.item_count}
                    </div>
                    {block.details && (
                      <div className="text-xs text-gray-500 mt-1">{block.details}</div>
                    )}
                  </td>

                  {/* Source */}
                  <td className="py-4 px-5">
                    <div className="text-sm text-gray-300">{block.source}</div>
                    {block.is_curated && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">
                        Curated
                      </span>
                    )}
                    {block.error && (
                      <div className="mt-1 text-xs text-red-400 max-w-xs truncate" title={block.error}>
                        Error: {block.error}
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="py-4 px-5 text-center">
                    {block.admin_link ? (
                      <Link
                        to={block.admin_link}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 hover:text-white transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        Manage
                      </Link>
                    ) : (
                      <span className="text-gray-600 text-sm">Auto-generated</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <Link
            to="/admin/page-builder"
            className="flex items-center gap-3 p-4 bg-gray-900/50 border border-gray-800 rounded-xl hover:border-orange-500/50 transition-colors group"
          >
            <div className="p-2 bg-orange-500/20 rounded-lg group-hover:bg-orange-500/30 transition-colors">
              <LayoutDashboard className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <div className="font-medium text-white">Page Builder</div>
              <div className="text-xs text-gray-500">Edit homepage layout</div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-500 ml-auto" />
          </Link>

          <Link
            to="/admin/site-manager"
            className="flex items-center gap-3 p-4 bg-gray-900/50 border border-gray-800 rounded-xl hover:border-orange-500/50 transition-colors group"
          >
            <div className="p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
              <Settings className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="font-medium text-white">Site Manager</div>
              <div className="text-xs text-gray-500">Manage curated content</div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-500 ml-auto" />
          </Link>

          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-gray-900/50 border border-gray-800 rounded-xl hover:border-orange-500/50 transition-colors group"
          >
            <div className="p-2 bg-green-500/20 rounded-lg group-hover:bg-green-500/30 transition-colors">
              <ExternalLink className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <div className="font-medium text-white">View Homepage</div>
              <div className="text-xs text-gray-500">Open in new tab</div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-500 ml-auto" />
          </a>
        </div>

        {/* No Layout State */}
        {healthData?.status === 'no_layout' && (
          <div className="mt-8 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-yellow-400 mb-2">No Homepage Layout Published</h3>
            <p className="text-gray-400 mb-4">
              The homepage is using the default layout. Publish a layout from Page Builder to customize it.
            </p>
            <Link to="/admin/page-builder">
              <Button>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Open Page Builder
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHomepageHealth;
